import React, { useEffect, useState } from "react";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { formatDistanceToNow } from "date-fns";
import { supabase } from "@/integrations/supabase/client";
import { BoostProfileModal } from "@/components/profile/BoostProfileModal";
import { Rocket } from "lucide-react";
import { useInView } from "react-intersection-observer";
import { useTranslation } from "react-i18next";
import { FollowButton } from "@/components/profile/shared/FollowButton";
import { useQuery } from "@tanstack/react-query";
import type { PortfolioItem } from "@/components/profile/portfolio/types";
import { Link } from "react-router-dom";

const FeedItem = ({ project, isBoosted }: { project: PortfolioItem; isBoosted: boolean }) => {
  const { t } = useTranslation();
  const { ref, inView } = useInView({
    threshold: 0.5,
    triggerOnce: false
  });

  const { data: currentUser } = useQuery({
    queryKey: ["currentUser"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      return user;
    },
  });

  const profileId = project.portfolios?.profiles?.id;

  return (
    <Card key={project.id} className="overflow-hidden hover:shadow-lg transition-shadow mb-4 sm:mb-6">
      <CardHeader className="pb-2 sm:pb-4">
        <div className="flex items-center space-x-3">
          <Link 
            to={`/profile/${profileId}?visitor=true`} 
            className="h-8 w-8 sm:h-10 sm:w-10 rounded-lg overflow-hidden flex-shrink-0"
          >
            <Avatar className="h-full w-full rounded-lg">
              <AvatarImage 
                src={project.portfolios?.profiles?.avatar_url || '/placeholder.svg'} 
                className="object-cover"
              />
              <AvatarFallback className="rounded-lg text-xs sm:text-sm">
                {project.portfolios?.profiles?.full_name?.[0] || t('feed.anonymous')}
              </AvatarFallback>
            </Avatar>
          </Link>
          <div className="flex-1 min-w-0 flex flex-col">
            <div className="flex items-center gap-2">
              <Link 
                to={`/profile/${profileId}?visitor=true`}
                className="hover:underline"
              >
                <h3 className="font-semibold text-sm sm:text-base truncate">
                  {project.portfolios?.profiles?.full_name || t('feed.anonymous')}
                </h3>
              </Link>
              {currentUser && profileId && currentUser.id !== profileId && (
                <FollowButton 
                  profileId={profileId} 
                  currentUserId={currentUser.id}
                />
              )}
            </div>
            {project.portfolios?.profiles?.category && (
              <span className="text-xs text-muted-foreground">
                {project.portfolios.profiles.category.name}
              </span>
            )}
          </div>
          {isBoosted && (
            <>
              <Badge 
                variant="secondary" 
                className="ml-auto text-[10px] whitespace-nowrap bg-[#9b87f5] text-white hover:bg-[#9b87f5] hidden sm:flex items-center px-2 py-0.5"
              >
                BOOSTED
              </Badge>
              <Badge 
                variant="secondary" 
                className="ml-auto bg-[#9b87f5] text-white hover:bg-[#9b87f5] sm:hidden p-1"
              >
                <Rocket className="w-3 h-3" />
              </Badge>
            </>
          )}
        </div>
      </CardHeader>
      <CardContent className="p-0">
        {project.media_url && (
          <div className="aspect-video sm:aspect-[4/3] relative overflow-hidden" ref={ref}>
            {project.media_type?.startsWith('video') ? (
              <video 
                src={project.media_url}
                className="object-cover w-full h-full"
                controls
                loop
                muted
                playsInline
                autoPlay={inView}
                onCanPlay={(e) => {
                  const video = e.target as HTMLVideoElement;
                  if (inView) {
                    video.play().catch(() => {
                      console.log('Video playback failed');
                    });
                  } else {
                    video.pause();
                  }
                }}
              />
            ) : (
              <img 
                src={project.media_url} 
                alt={project.title}
                className="object-cover w-full h-full"
              />
            )}
          </div>
        )}
        <div className="px-3 sm:px-4 py-3 sm:py-4">
          <h4 className="font-semibold text-base sm:text-lg mb-1 sm:mb-2 line-clamp-2">
            {project.title}
          </h4>
          <p className="text-xs sm:text-sm text-muted-foreground line-clamp-2 sm:line-clamp-3">
            {project.description}
          </p>
          <p className="text-xs text-muted-foreground mt-2 sm:mt-4">
            {t('feed.posted')} {formatDistanceToNow(new Date(project.created_at), { addSuffix: true })}
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

const Feed: React.FC = () => {
  const [projects, setProjects] = useState<PortfolioItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [showBoostModal, setShowBoostModal] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        console.log("Fetching projects...");
        const { data, error } = await supabase
          .from('portfolio_items')
          .select(`
            *,
            portfolios(
              profiles(
                full_name,
                avatar_url,
                job_title,
                category:artist_categories(name),
                id
              )
            )
          `)
          .order('created_at', { ascending: false });

        if (error) {
          console.error("Error fetching projects:", error);
          return;
        }

        console.log("Fetched projects:", data);
        
        const sortedProjects = (data || []).sort((a, b) => {
          const aIsBoosted = data?.indexOf(a) < 2;
          const bIsBoosted = data?.indexOf(b) < 2;
          if (aIsBoosted && !bIsBoosted) return -1;
          if (!aIsBoosted && bIsBoosted) return 1;
          return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
        });
        
        setProjects(sortedProjects);
      } catch (error) {
        console.error("Error in fetchProjects:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchProjects();
  }, []);

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-4">
        <div className="text-center">{t('feed.loading')}</div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-2 sm:px-4 py-4 max-w-2xl">
      <div className="flex items-center gap-3 mb-4 relative z-50">
        <Button 
          className="bg-[#9b87f5] hover:bg-[#7E69AB] text-white text-xs py-2 h-8"
          onClick={() => setShowBoostModal(true)}
        >
          <Rocket className="w-3 h-3 mr-1" />
          <span className="whitespace-nowrap">{t('feed.boostProject')}</span>
        </Button>
      </div>

      <div className="space-y-4 sm:space-y-6">
        {projects.map((project, index) => (
          <FeedItem 
            key={project.id} 
            project={project} 
            isBoosted={index < 2}
          />
        ))}
        {projects.length === 0 && (
          <div className="text-center text-muted-foreground p-4">
            {t('feed.noProjects')}
          </div>
        )}
      </div>

      <BoostProfileModal 
        isOpen={showBoostModal}
        onClose={() => setShowBoostModal(false)}
        userRole="talent"
      />
    </div>
  );
};

export default Feed;
